import { zodResolver } from "@hookform/resolvers/zod";
import { get } from "lodash";
import { FC, useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { FileUploadProvider } from "@/components/file-upload-context";
import { FileUploadInput } from "@/components/sku/file-upload-input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { AddTrackButton } from "@/components/web-experience/audio-tab/add-track-button";
import { useCMSStore } from "@/hooks/use-cms-store";
import { FileType, type UploadedFile } from "@/lib/uploads";

import { useCMSTabContext } from "../cms-tab-context";
import { useAudioTabContext } from "./audio-tab-context";
import { AudioTracks } from "./audio-tracks";

const audioTabPlaylistFormSchema = z.object({
  cover_image_url: z.union([z.url(), z.literal("")]),
  is_downloadable_all: z.boolean(),
});

export const AudioTabPlaylist: FC = () => {
  const { tabIndex } = useCMSTabContext();
  const { options, data } = useAudioTabContext();
  const onUpdate = useCMSStore.use.onUpdateAudioTabOptions();
  const onAdd = useCMSStore.use.onAddPlaylistTracks();
  const onUpdatePlaylistTracks = useCMSStore.use.onUpdatePlaylistTracks();
  const setValidationErrors = useCMSStore.use.setValidationErrors();

  const form = useForm<z.infer<typeof audioTabPlaylistFormSchema>>({
    resolver: zodResolver(audioTabPlaylistFormSchema),
    defaultValues: {
      cover_image_url: options.cover_image_url ?? "",
      is_downloadable_all: get(options, "is_downloadable_all", false),
    },
  });

  const {
    formState: { isValid },
  } = form;

  useEffect(() => {
    setValidationErrors(!isValid);
  }, [isValid, setValidationErrors]);

  useEffect(() => {
    const unsubscribe = form.subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        onUpdate(tabIndex, { ...options, ...values });
      },
    });

    return unsubscribe;
  }, [form, onUpdate, options, tabIndex]);

  const allDownloadable = useMemo(
    () =>
      data.length > 0 && data.every((track) => track?.options?.is_downloadable),
    [data],
  );

  useEffect(() => {
    if (data.length === 0) return;
    if (form.getValues("is_downloadable_all") !== allDownloadable) {
      form.setValue("is_downloadable_all", allDownloadable, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [allDownloadable, data.length, form]);

  const handleFileUploadFinish = useCallback(
    (uploadedFiles: UploadedFile[]) => {
      onAdd(
        tabIndex,
        uploadedFiles.map(({ fileName, url }) => ({
          file_name: fileName,
          url,
        })),
      );
    },
    [onAdd, tabIndex],
  );

  const handlePlaylistDownloadableChange = useCallback(
    (checked: boolean) => {
      const updates = data.map((_, index) => ({
        trackIndex: index,
        options: { is_downloadable: checked },
      }));
      onUpdatePlaylistTracks(tabIndex, updates);
    },
    [data, onUpdatePlaylistTracks, tabIndex],
  );

  return (
    <Card>
      <CardHeader>
        <h4 className="text-lg font-medium leading-none">Playlist</h4>
      </CardHeader>
      <Form {...form}>
        <CardContent>
          <FormField
            control={form.control}
            name="cover_image_url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cover Image</FormLabel>
                <FormControl>
                  <FileUploadInput {...field} fileType={FileType.IMAGE} />
                </FormControl>
                <FormDescription>
                  Playlist cover image. The image will be cropped to fit the
                  display.
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="is_downloadable_all"
            render={({ field }) => (
              <FormItem className="flex items-start w-full mt-5">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      handlePlaylistDownloadableChange(checked);
                    }}
                  />
                </FormControl>
                <div className="flex flex-col gap-y-2">
                  <FormLabel>Download Playlist</FormLabel>
                  <FormDescription>
                    Allow all tracks in the playlist to be downloadable
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />

          <div className="space-y-4 mt-4">
            <p className="text-sm font-medium">Tracks</p>
            <AudioTracks tracks={data} />
            <FileUploadProvider multiple onFinish={handleFileUploadFinish}>
              <AddTrackButton />
            </FileUploadProvider>
          </div>
        </CardContent>
      </Form>
    </Card>
  );
};
